<template>
    <v-container fluid>
        <v-row>
            <v-col cols="12">
                <h2 class="primary--text">Consulta de diárias</h2>
                <v-breadcrumbs
                    large
                    :items="crumbs"
                    divider="/"
                    class="pl-0"
                ></v-breadcrumbs>
            </v-col>
        </v-row>

        <!-- LEGENDA -->
        <v-row v-if="primaveraIntegrationActive === true">
            <v-col cols="12">
                <div class="d-flex flex-wrap align-center legend-wrapper">
                    <div class="legend-pill mr-2 mb-2">
                        <v-icon small class="mr-1 black--text">mdi-account</v-icon>
                        <span>Gerado por Encarregado</span>
                    </div>

                    <div class="legend-pill mr-2 mb-2">
                        <v-icon small class="mr-1 orange--text text--darken-2">mdi-steering</v-icon>
                        <span>Criado por Manobrador</span>
                    </div>

                    <div class="legend-pill mr-2 mb-2">
                        <v-icon small class="mr-1 blue--text text--darken-2">mdi-briefcase</v-icon>
                        <span>Com KPI</span>
                    </div>

                    <div class="legend-pill mr-2 mb-2">
                        <v-icon small class="mr-1 green--text">mdi-check-circle</v-icon>
                        <span>Aprovados</span>
                    </div>

                    <div class="legend-pill mr-2 mb-2">
                        <v-icon small class="mr-1 purple--text">mdi-cloud-upload</v-icon>
                        <span>Pode pedir integração</span>
                    </div>

                    <div class="legend-pill mr-2 mb-2">
                        <v-icon small class="mr-1 grey--text text--darken-1">mdi-timer-sand</v-icon>
                        <span>Integração em fila</span>
                    </div>

                    <div class="legend-pill mr-2 mb-2">
                        <v-icon small class="mr-1 red--text">mdi-sync-alert</v-icon>
                        <span>Erro na Integração</span>
                    </div>
                </div>
            </v-col>
        </v-row>

        <v-row>
            <v-col cols="12" md="4">
                <v-autocomplete
                    :items="obras"
                    v-model="filter.work_id"
                    outlined
                    dense
                    :item-text="item => item.Codigo + ' - ' + item.Descricao"
                    item-value="Id"
                    label="Obra a procurar"
                />
            </v-col>

            <v-col cols="12" md="3">
                <v-text-field
                    v-model="filter.start_date"
                    label="Data início"
                    type="date"
                    outlined
                    dense
                />
            </v-col>

            <v-col cols="12" md="3">
                <v-text-field
                    v-model="filter.end_date"
                    label="Data fim"
                    type="date"
                    outlined
                    dense
                />
            </v-col>

            <v-col cols="12" md="1">
                <v-btn
                    :disabled="filter.work_id == null"
                    @click="searchButton"
                    small
                    fab
                    class="success"
                >
                    <v-icon>mdi-magnify</v-icon>
                </v-btn>
            </v-col>
        </v-row>

        <v-row v-if="calendarError">
            <v-col cols="12">
                <v-alert
                    dense
                    outlined
                    type="error"
                    class="mb-2"
                >
                    {{ calendarError }}
                </v-alert>
            </v-col>
        </v-row>

        <v-row v-if="integrationMessage">
            <v-col cols="12">
                <v-alert
                    dense
                    outlined
                    type="success"
                    class="mb-2"
                >
                    {{ integrationMessage }}
                </v-alert>
            </v-col>
        </v-row>

        <v-row v-if="integrationError">
            <v-col cols="12">
                <v-alert
                    dense
                    outlined
                    type="error"
                    class="mb-2"
                >
                    {{ integrationError }}
                </v-alert>
            </v-col>
        </v-row>

        <!-- OBRA SEM INTEGRACAO PRIMAVERA -->
        <v-row v-if="primaveraIntegrationActive === false && !loading">
            <v-col cols="12">
                <v-card outlined class="integration-unavailable-card pa-8 text-center">
                    <div class="integration-unavailable-illustration mb-5">
                        <v-icon size="92" color="blue-grey lighten-1">mdi-cloud-off-outline</v-icon>
                    </div>
                    <h3 class="mb-3">Integração com o Primavera indisponível</h3>
                    <p class="integration-unavailable-text mb-0">
                        A obra não está ativa para integração com o primavera. Por favor contacte o suporte para proceder à ativação
                    </p>
                </v-card>
            </v-col>
        </v-row>

        <!-- CALENDÁRIO -->
        <v-row v-if="primaveraIntegrationActive === true && calendarMonths.length">
            <v-col cols="12">
                <v-card outlined class="calendar-card mb-4">
                    <v-card-title class="calendar-title">
                        Calendário de estados

                        <v-spacer></v-spacer>

                        <div class="d-flex flex-wrap align-center calendar-legend">
                            <div class="calendar-legend-item">
                                <span class="calendar-dot calendar-dot-none"></span>
                                <span>Sem diária</span>
                            </div>

                            <div class="calendar-legend-item">
                                <span class="calendar-dot calendar-dot-red"></span>
                                <span>Primavera sem ODC</span>
                            </div>

                            <div class="calendar-legend-item">
                                <span class="calendar-dot calendar-dot-yellow"></span>
                                <span>Por aprovar</span>
                            </div>

                            <div class="calendar-legend-item">
                                <span class="calendar-dot calendar-dot-green"></span>
                                <span>Aprovado</span>
                            </div>

                            <div class="calendar-legend-item">
                                <span class="calendar-dot calendar-dot-purple"></span>
                                <span>Integrado</span>
                            </div>
                        </div>
                    </v-card-title>

                    <v-card-text>
                        <v-row>
                            <v-col
                                v-for="month in calendarMonths"
                                :key="month.key"
                                cols="12"
                                md="6"
                            >
                                <div class="calendar-month">
                                    <div class="calendar-month-header">
                                        {{ month.label }}
                                    </div>

                                    <div class="calendar-weekdays">
                                        <div
                                            v-for="weekday in weekdays"
                                            :key="weekday"
                                            class="calendar-weekday"
                                        >
                                            {{ weekday }}
                                        </div>
                                    </div>

                                    <div class="calendar-grid">
                                        <div
                                            v-for="(day, index) in month.days"
                                            :key="month.key + '-' + index"
                                            class="calendar-day"
                                            :class="getCalendarDayClass(day)"
                                            :title="getCalendarDayTitle(day)"
                                        >
                                            <template v-if="day">
                                                <div class="calendar-day-number">
                                                    {{ day.day }}
                                                </div>

                                                <div
                                                    v-if="getCalendarStatusForDate(day.date)"
                                                    class="calendar-day-label"
                                                >
                                                    {{ getCalendarShortLabel(getCalendarStatusForDate(day.date)) }}
                                                </div>

                                                <div
                                                    v-if="selectedWorkAllowsManualIntegration && (canRequestIntegrationForDate(day.date) || hasActiveIntegrationJobForDate(day.date))"
                                                    class="calendar-day-action"
                                                >
                                                    <v-btn
                                                        x-small
                                                        :color="hasActiveIntegrationJobForDate(day.date) ? 'grey darken-1' : 'success'"
                                                        class="calendar-integrate-btn"
                                                        :loading="isIntegrationLoading(day.date)"
                                                        :disabled="isIntegrationLoading(day.date) || hasActiveIntegrationJobForDate(day.date)"
                                                        @click.stop="requestIntegration(day.date)"
                                                    >
                                                        {{ hasActiveIntegrationJobForDate(day.date) ? 'Em fila' : 'Integrar' }}
                                                    </v-btn>
                                                </div>
                                            </template>
                                        </div>
                                    </div>
                                </div>
                            </v-col>
                        </v-row>
                    </v-card-text>
                </v-card>
            </v-col>
        </v-row>

        <v-row v-if="loading">
            <v-col cols="12">
                <v-progress-linear
                    indeterminate
                    color="primary"
                    rounded
                />
            </v-col>
        </v-row>

        <v-row v-if="primaveraIntegrationActive === true && groupedDiaries.length">
            <v-col cols="12">
                <v-simple-table>
                    <thead>
                        <tr>
                            <th>Data</th>
                            <th>Total de diárias</th>
                            <th style="width: 50px;" class="text-right"></th>
                        </tr>
                    </thead>

                    <tbody>
                        <template v-for="group in groupedDiaries">
                            <tr :key="group.date" class="primary white--text">
                                <td>
                                    <div class="d-flex align-center">
                                        <span>{{ formatDate(group.date) }}</span>

                                        <v-btn
                                            small
                                            color="success"
                                            class="ml-3 elevation-5"
                                            v-if="getApprovableDiaries(group.diaries).length !== 0"
                                            @click="confirmApproveAll(group)"
                                        >
                                            Aprovar tudo
                                        </v-btn>
                                    </div>
                                </td>

                                <td>
                                    <div class="d-flex align-center">
                                        <span>{{ group.diaries.length }}</span>
                                    </div>
                                </td>

                                <td class="text-right" width="50">
                                    <div class="d-flex align-center justify-end">
                                        <v-icon
                                            v-if="isGroupFullyApproved(group)"
                                            small
                                            class="mr-2 green--text"
                                            title="Dia totalmente aprovado"
                                        >
                                            mdi-check-circle
                                        </v-icon>

                                        <v-btn icon @click="toggleRow(group.date)">
                                            <v-icon color="white">
                                                {{ isExpanded(group.date) ? 'mdi-chevron-up' : 'mdi-chevron-down' }}
                                            </v-icon>
                                        </v-btn>
                                    </div>
                                </td>
                            </tr>

                            <tr
                                v-if="isExpanded(group.date)"
                                :key="group.date + '-expanded'"
                            >
                                <td colspan="3" class="pa-0">
                                    <v-simple-table dense>
                                        <thead>
                                            <tr>
                                                <th>Status</th>
                                                <th>Obra</th>
                                                <th>Data</th>
                                                <th>Resp. Aprovação</th>
                                                <th>Criada Em</th>
                                                <th>Criada Por</th>
                                                <th>Opções</th>
                                            </tr>
                                        </thead>

                                        <tbody>
                                            <tr
                                                v-for="diary in group.diaries"
                                                :key="diary.id"
                                            >
                                                <td>
                                                    <div class="d-flex align-center">
                                                        <v-icon
                                                            small
                                                            class="mr-2"
                                                            :class="getRoleIconColorClass(diary)"
                                                        >
                                                            {{ getRoleIcon(diary) }}
                                                        </v-icon>

                                                        <span class="mr-2">
                                                            <v-icon small color="success" v-if="getApprovalText(diary) == 'total'">mdi-check-decagram</v-icon>
                                                            <span v-else>{{ getApprovalText(diary) }}</span>
                                                        </span>

                                                        <v-icon
                                                            v-if="hasKpi(diary)"
                                                            small
                                                            class="mr-2 blue--text text--darken-2"
                                                            title="Com KPI"
                                                        >
                                                            mdi-briefcase
                                                        </v-icon>

                                                        <v-icon
                                                            v-if="isApproved(diary)"
                                                            small
                                                            class="green--text"
                                                            title="Totalmente aprovada"
                                                        >
                                                            mdi-check-circle
                                                        </v-icon>

                                                        <v-icon
                                                            v-if="diary.integration_error != null"
                                                            small
                                                            class="red--text"
                                                            title="Erro na Integração"
                                                        >
                                                            mdi-sync-alert
                                                        </v-icon>
                                                    </div>
                                                </td>

                                                <td>{{ getWorkCode(diary) }}</td>

                                                <td>{{ diary.data }}</td>

                                                <td>{{ getApprovalResponsible(diary) }}</td>

                                                <td>{{ diary.created_at }}</td>

                                                <td>{{ diary.creator }}</td>

                                                <td>
                                                    <div class="d-flex align-center">
                                                        <template v-if="canEditOrPreview(diary)">
                                                            <v-btn
                                                                fab
                                                                dark
                                                                x-small
                                                                outlined
                                                                color="warning"
                                                                class="mr-2"
                                                                @click="edit(diary)"
                                                            >
                                                                <v-icon dark>
                                                                    mdi-pencil
                                                                </v-icon>
                                                            </v-btn>

                                                            <v-btn
                                                                fab
                                                                x-small
                                                                outlined
                                                                color="blue"
                                                                class="mr-2"
                                                                @click="openPreviewDiary(diary.id)"
                                                            >
                                                                <v-icon>mdi-magnify</v-icon>
                                                            </v-btn>

                                                            <v-btn
                                                                fab
                                                                dark
                                                                x-small
                                                                outlined
                                                                :color="checkIfCanApprove(diary) == null ? 'grey' : 'success'"
                                                                class="mr-2"
                                                                v-if="checkIfCanApprove(diary) == true || checkIfCanApprove(diary) == null"
                                                                @click="confirmApprove(diary)"
                                                            >
                                                                <v-icon dark>
                                                                    mdi-check-decagram
                                                                </v-icon>
                                                            </v-btn>
                                                        </template>

                                                        <template v-else>
                                                            <span class="grey--text">-</span>
                                                        </template>
                                                    </div>
                                                </td>
                                            </tr>
                                        </tbody>
                                    </v-simple-table>
                                </td>
                            </tr>
                        </template>
                    </tbody>
                </v-simple-table>
            </v-col>
        </v-row>

        <v-dialog width="80%" height="80%" v-model="previewDiary.show">
            <v-card>
                <PreviewDiary v-if="previewDiary.show" :diary_id="previewDiary.id" @close="previewDiary.show = false"/>
            </v-card>
        </v-dialog>

        <DialogConfirmation
            v-if="confirmationModal.show"
            :title="confirmationModal.title"
            type="warning"
            :opened="confirmationModal.show"
            :elevation="0"
            @on-submit="approveFromModal"
            @on-cancel="confirmationModal.item = null; confirmationModal.show = false;">
                {{ confirmationModal.message }}
        </DialogConfirmation>
    </v-container>
</template>

<script>
import Diaries from "@/api/Diaries.js"
import Obra from "@/api/Obra.js"
import User from "@/api/User.js"
import PreviewDiary from "@/views/Diaries/Preview/PreviewDiary.vue"
import DialogConfirmation from '@/components/UI/Modals/DialogConfirmation.vue'
import Approval from "@/api/Approval.js"
import Integrations from "@/api/Integrations.js"

export default {
    name: "DiariesAggregator",
    components:{
        PreviewDiary,
        DialogConfirmation
    },
    data() {
        return {
            items: [],
            calendarStatuses: [],
            calendarMonths: [],
            calendarError: null,
            primaveraIntegrationActive: null,
            manualIntegrationActive: false,
            integrationError: null,
            integrationMessage: null,
            integrationLoadingDays: [],
            obras: [],
            obras_user_tem_acesso: [],
            loading: false,
            expandedRows: [],
            weekdays: ['Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb', 'Dom'],
            filter: {
                start_date: (() => {
                    const d = new Date()
                    d.setDate(d.getDate() - 8)
                    return d.toISOString().slice(0, 10)
                })(),
                end_date: new Date().toISOString().slice(0, 10),
                work_id: null
            },
            previewDiary:{
                id: null,
                show: false
            },
            confirmationModal:{
                show: false,
                title: '',
                message: '',
                item: null
            },
        }
    },

    mounted() {
        this.fillBaseData()
    },

    methods: {
        isTruthy(value) {
            return value === true || value === 1 || value === '1'
        },

        isGroupFullyApproved(group) {
            if (!group || !group.diaries || !group.diaries.length) {
                return false
            }

            return group.diaries.every(diary => this.isApproved(diary))
        },

        async approve() {
            if (!this.confirmationModal.item) {
                this.confirmationModal.show = false
                return
            }

            try {
                const approval = this.confirmationModal.item

                await Approval.approve({
                    webAppLinkHash: approval.webAppLinkHash,
                    id_user_creator: approval.id_user_creator,
                    creation_role: approval.creation_role,
                    hash: approval.hash,
                    chave: approval.chave
                })

                this.confirmationModal.item = null
                this.confirmationModal.show = false

                await this.searchButton()
            } catch (e) {
                console.error(e)
                this.confirmationModal.item = null
                this.confirmationModal.show = false
            }
        },

        getApprovableDiaries(diaries) {
            return diaries.filter(diary => {
                return this.canEditOrPreview(diary) && this.checkIfCanApprove(diary) === true
            })
        },

        confirmApproveAll(group) {
            const diaries = this.getApprovableDiaries(group.diaries)

            if (!diaries.length) {
                return
            }

            this.confirmationModal.item = {
                type: 'multiple',
                diaries
            }
            this.confirmationModal.title = 'Tem a certeza?'
            this.confirmationModal.message = `Tem a certeza que pretende aprovar ${diaries.length} diária(s) de ${group.date}?`
            this.confirmationModal.show = true
        },

        async approveMultiple(diaries) {
            const payload = {
                approvals: diaries.map(diary => ({
                    webAppLinkHash: diary.webAppLinkHash,
                    id_user_creator: diary.id_user_creator,
                    creation_role: diary.creation_role
                })),
                hash: diaries[0]?.hash,
                chave: diaries[0]?.chave
            }

            await Approval.multipleApproval(payload)
        },

        async approveFromModal() {
            if (!this.confirmationModal.item) {
                this.confirmationModal.show = false
                return
            }

            try {
                if (this.confirmationModal.item.type === 'multiple') {
                    await this.approveMultiple(this.confirmationModal.item.diaries)
                } else {
                    const approval = this.confirmationModal.item

                    await Approval.approve({
                        webAppLinkHash: approval.webAppLinkHash,
                        id_user_creator: approval.id_user_creator,
                        creation_role: approval.creation_role,
                        hash: approval.hash,
                        chave: approval.chave
                    })
                }

                this.confirmationModal.item = null
                this.confirmationModal.show = false

                await this.searchButton()
            } catch (e) {
                console.error(e)
                this.confirmationModal.item = null
                this.confirmationModal.show = false
            }
        },

        confirmApprove(approval){
            // Esta linha é a que impede a aprovação com a integração do JOSÉ ALVES
            /*if(approval.all_lines_with_costs == false){
                return
            }*/

            this.confirmationModal.item = approval
            this.confirmationModal.title = 'Tem a certeza?'
            this.confirmationModal.message = 'Tem a certeza que pretende aprovar a diária?'
            this.confirmationModal.show = true
        },

        checkIfCanApprove(item){
            let approval = item.current_approval

            // Esta linha é a que impede a aprovação com a integração do JOSÉ ALVES
            /*if(item.all_lines_with_costs == false)
                return null;*/

            // Verficar primeiro o role... Se não tiver role de encarregado, administrativo ou engenheiro, não pode aprovar
            if(!this.$store.getters.hasRole(['encarregado', 'administrativo', 'engenheiro', 'parque']))
                return false

            // Verificar se tem acesso a estas obras. Se não tiver, não pode aprovar
            let acesso_user = []

            this.obras_user_tem_acesso.forEach(element => {
                acesso_user.push(element.Codigo)
            });

            if(!acesso_user.includes(item.chave))
                if(!this.$store.getters.hasRole(['parque']))
                    return false;

            // verificar o nível que é necessário
            // se for de 3 camadas, é um encarregado
            if(parseInt(item.level_needed_to_approve) == 3){
                // Se está a null, significa que é preciso ser aprovado pelo ENCARREGADO
                if(approval == null){
                    if(this.$store.getters.hasRole(['administrativo', 'engenheiro', 'parque']))
                        return false

                    return true
                }

                if(parseInt(approval.level) == 0){
                    return this.$store.getters.hasRole(['encarregado'])
                }

                // Se tem approval, verificar qual o role de aprovação.
                // Se estiver a 1, significa que o ADMINISTRATIVO já aprovou
                if(approval.level == 1){
                    return this.$store.getters.hasRole(['administrativo'])
                }

                if(parseInt(approval.level) == 2){
                    if(this.$store.getters.hasRole(['parque']))
                        if(item.parque_approved)
                            return true
                        else
                            return false
                }

                // Se tem approval, verificar qual o role de aprovação.
                // Se estiver a 1, significa que o ADMINISTRATIVO já aprovou
                if(approval.level == 2){
                    return this.$store.getters.hasRole(['engenheiro'])
                }

                // Se estiver a 2, significa que já foi totalmente aprovado e não precisa de mais aprovações
                return false
            }else{

                // Verficar primeiro o role... Se não tiver role de encarregado, administrativo ou engenheiro, não pode aprovar
                if(!this.$store.getters.hasRole(['administrativo', 'engenheiro', 'parque']))
                    return false

                // Se está a null, significa que é preciso ser aprovado pelo ADMINISTRATIVO
                if(approval == null){
                    if(this.$store.getters.hasRole(['engenheiro']))
                        return false

                    if(this.$store.getters.hasRole(['parque']))
                        return false

                    return true
                }

                if(parseInt(approval.level) == 0){
                    return this.$store.getters.hasRole(['administrativo'])
                }

                if(parseInt(approval.level) == 1){
                    if(this.$store.getters.hasRole(['parque'])){
                        if(item.parque_approved)
                            return false
                        else
                            return true
                    }
                }

                // Se tem approval, verificar qual o role de aprovação.
                // Se estiver a 1, significa que o ADMINISTRATIVO já aprovou
                if(approval.level == 1){
                    return this.$store.getters.hasRole(['engenheiro'])
                }

                // Se estiver a 2, significa que já foi totalmente aprovado e não precisa de mais aprovações
                return false
            }
        },

        async searchButton() {
            this.primaveraIntegrationActive = null
            this.manualIntegrationActive = false
            this.calendarError = null
            this.integrationError = null
            this.integrationMessage = null

            const validation = this.validateDateRange()

            if (!validation.valid) {
                this.calendarError = validation.message
                this.items = []
                this.calendarStatuses = []
                this.calendarMonths = []
                this.expandedRows = []
                return
            }

            this.loading = true

            try {
                const resp = await Diaries.searchDiariesAggregator(this.filter)

                this.primaveraIntegrationActive = resp.data?.primavera_integration_active === true
                this.manualIntegrationActive = resp.data?.manual_integration_active === true

                if (Array.isArray(resp.data)) {
                    this.items = resp.data || []
                    this.calendarStatuses = []
                } else {
                    this.items = resp.data?.diaries || []
                    this.calendarStatuses = resp.data?.calendar_statuses || resp.data?.calendarStatuses || []
                }

                this.calendarMonths = this.buildCalendarMonths(this.filter.start_date, this.filter.end_date)
                this.expandedRows = []
            } catch (e) {
                console.error(e)
                this.items = []
                this.calendarStatuses = []
                this.calendarMonths = []
                this.expandedRows = []
                this.primaveraIntegrationActive = null
                this.calendarError = 'Não foi possível consultar o estado de integração da obra.'
            } finally {
                this.loading = false
            }
        },

        validateDateRange() {
            if (!this.filter.start_date || !this.filter.end_date) {
                return {
                    valid: false,
                    message: 'Tem de preencher a data inicial e a data final.'
                }
            }

            const start = this.parseLocalDate(this.filter.start_date)
            const end = this.parseLocalDate(this.filter.end_date)

            if (!start || !end) {
                return {
                    valid: false,
                    message: 'As datas indicadas são inválidas.'
                }
            }

            if (start > end) {
                return {
                    valid: false,
                    message: 'A data inicial não pode ser superior à data final.'
                }
            }

            const months = this.getMonthsBetweenDates(this.filter.start_date, this.filter.end_date)

            if (months.length > 2) {
                return {
                    valid: false,
                    message: 'Não é permitido pesquisar mais do que 2 meses.'
                }
            }

            return {
                valid: true,
                message: null
            }
        },

        parseLocalDate(value) {
            if (!value) return null

            const parts = value.split('-')

            if (parts.length !== 3) return null

            const year = parseInt(parts[0])
            const month = parseInt(parts[1]) - 1
            const day = parseInt(parts[2])

            return new Date(year, month, day)
        },

        formatLocalDate(date) {
            const year = date.getFullYear()
            const month = String(date.getMonth() + 1).padStart(2, '0')
            const day = String(date.getDate()).padStart(2, '0')

            return `${year}-${month}-${day}`
        },

        getMonthsBetweenDates(startDate, endDate) {
            const start = this.parseLocalDate(startDate)
            const end = this.parseLocalDate(endDate)

            if (!start || !end) {
                return []
            }

            const months = []
            const cursor = new Date(start.getFullYear(), start.getMonth(), 1)
            const last = new Date(end.getFullYear(), end.getMonth(), 1)

            while (cursor <= last) {
                months.push({
                    year: cursor.getFullYear(),
                    month: cursor.getMonth()
                })

                cursor.setMonth(cursor.getMonth() + 1)
            }

            return months
        },

        buildCalendarMonths(startDate, endDate) {
            const months = this.getMonthsBetweenDates(startDate, endDate)

            return months.map(month => this.buildCalendarMonth(month.year, month.month, startDate, endDate))
        },

        buildCalendarMonth(year, month, startDate, endDate) {
            const start = this.parseLocalDate(startDate)
            const end = this.parseLocalDate(endDate)

            const firstDay = new Date(year, month, 1)
            const daysInMonth = new Date(year, month + 1, 0).getDate()

            // Semana a começar à segunda-feira.
            const leadingEmptyDays = (firstDay.getDay() + 6) % 7

            const days = []

            for (let i = 0; i < leadingEmptyDays; i++) {
                days.push(null)
            }

            for (let day = 1; day <= daysInMonth; day++) {
                const date = new Date(year, month, day)
                const formattedDate = this.formatLocalDate(date)

                days.push({
                    day,
                    date: formattedDate,
                    inRange: date >= start && date <= end
                })
            }

            while (days.length % 7 !== 0) {
                days.push(null)
            }

            return {
                key: `${year}-${String(month + 1).padStart(2, '0')}`,
                label: this.getMonthLabel(year, month),
                days
            }
        },

        getMonthLabel(year, month) {
            const months = [
                'Janeiro',
                'Fevereiro',
                'Março',
                'Abril',
                'Maio',
                'Junho',
                'Julho',
                'Agosto',
                'Setembro',
                'Outubro',
                'Novembro',
                'Dezembro'
            ]

            return `${months[month]} ${year}`
        },

        getCalendarStatusForDate(date) {
            if (!date) return null

            return this.calendarStatusMap[date] || null
        },

        getCalendarDayClass(day) {
            if (!day) {
                return 'calendar-day-empty'
            }

            const classes = []

            if (!day.inRange) {
                classes.push('calendar-day-outside-range')
            }

            const status = this.getCalendarStatusForDate(day.date)

            if (status && status.status_color) {
                classes.push(`calendar-day-${status.status_color}`)
            }

            if (status && !status.status_color) {
                classes.push('calendar-day-no-color')
            }

            if (status && status.has_active_integration_job) {
                classes.push('calendar-day-has-active-job')
            }

            return classes.join(' ')
        },

        getCalendarDayTitle(day) {
            if (!day) return ''

            const status = this.getCalendarStatusForDate(day.date)

            if (!status) {
                return day.date
            }

            const label = status.status_label || 'Apenas existe diária no ODC'

            if (status.has_active_integration_job) {
                return `${day.date} - ${label} - Integração em fila`
            }

            return `${day.date} - ${label}`
        },

        getCalendarShortLabel(status) {
            if (!status) return ''

            if (status.has_active_integration_job) return 'Em fila'
            if (status.status_color === 'red') return 'Primavera'
            if (status.status_color === 'yellow') return 'Por aprovar'
            if (status.status_color === 'green') return 'Aprovado'
            if (status.status_color === 'purple') return 'Integrado'

            return 'ODC'
        },

        normalizeCalendarStatus(status) {
            if (!status) return null

            return {
                id: status.id || null,
                obra_id: status.obra_id || null,
                date: status.date || status.data || null,

                has_odc_diary: Boolean(Number(status.has_odc_diary)),
                odc_all_approved: Boolean(Number(status.odc_all_approved)),

                has_primavera_lines: Boolean(Number(status.has_primavera_lines)),
                has_primavera_manual_lines: Boolean(Number(status.has_primavera_manual_lines)),
                all_primavera_lines_closed: Boolean(Number(status.all_primavera_lines_closed)),

                primavera_total_lines: parseInt(status.primavera_total_lines || 0),
                primavera_closed_lines: parseInt(status.primavera_closed_lines || 0),
                primavera_odc_lines: parseInt(status.primavera_odc_lines || 0),
                primavera_manual_lines: parseInt(status.primavera_manual_lines || 0),

                status_color: status.status_color || status.calendar_status_color || null,
                status_label: status.status_label || status.calendar_status_label || null,

                has_active_integration_job: Boolean(Number(status.has_active_integration_job)),
                active_integration_job: status.active_integration_job || null,
                can_request_integration: Boolean(Number(status.can_request_integration)),

                checked_at: status.checked_at || null
            }
        },

        getCalendarStatusFromDiary(diary) {
            if (!diary) return null

            if (diary.calendar_status) {
                return this.normalizeCalendarStatus(diary.calendar_status)
            }

            if (
                diary.calendar_status_color ||
                diary.calendar_status_label ||
                diary.has_primavera_lines ||
                diary.has_primavera_manual_lines ||
                diary.all_primavera_lines_closed ||
                diary.primavera_total_lines ||
                diary.primavera_closed_lines ||
                diary.primavera_odc_lines ||
                diary.primavera_manual_lines ||
                diary.has_active_integration_job ||
                diary.can_request_integration
            ) {
                return this.normalizeCalendarStatus({
                    id: null,
                    obra_id: diary.obra_id,
                    date: diary.data,
                    has_odc_diary: true,
                    odc_all_approved: diary.full_approved,
                    has_primavera_lines: diary.has_primavera_lines,
                    has_primavera_manual_lines: diary.has_primavera_manual_lines,
                    all_primavera_lines_closed: diary.all_primavera_lines_closed,
                    primavera_total_lines: diary.primavera_total_lines,
                    primavera_closed_lines: diary.primavera_closed_lines,
                    primavera_odc_lines: diary.primavera_odc_lines,
                    primavera_manual_lines: diary.primavera_manual_lines,
                    status_color: diary.calendar_status_color,
                    status_label: diary.calendar_status_label,
                    has_active_integration_job: diary.has_active_integration_job,
                    active_integration_job: diary.active_integration_job,
                    can_request_integration: diary.can_request_integration
                })
            }

            return null
        },

        canRequestIntegrationForDate(date) {
            if (!date || !this.filter.work_id) {
                return false
            }

            if (!this.selectedWorkAllowsManualIntegration) {
                return false
            }

            const status = this.getCalendarStatusForDate(date)

            if (!status) {
                return false
            }

            return status.can_request_integration === true
        },

        hasActiveIntegrationJobForDate(date) {
            if (!this.selectedWorkAllowsManualIntegration) {
                return false
            }

            const status = this.getCalendarStatusForDate(date)

            if (!status) {
                return false
            }

            return status.has_active_integration_job === true
        },

        isIntegrationLoading(date) {
            return this.integrationLoadingDays.includes(date)
        },

        async requestIntegration(date) {
            if (!this.filter.work_id || !date) {
                return
            }

            if (!this.selectedWorkAllowsManualIntegration) {
                return
            }

            if (this.isIntegrationLoading(date)) {
                return
            }

            if (this.hasActiveIntegrationJobForDate(date)) {
                return
            }

            this.integrationError = null
            this.integrationMessage = null
            this.integrationLoadingDays.push(date)

            try {
                await Integrations.createDiariesIntegrationJob({
                    obra_id: this.filter.work_id,
                    days: [
                        date
                    ]
                })

                this.integrationMessage = `Pedido de integração criado para o dia ${date}.`

                await this.searchButton()
            } catch (e) {
                console.error(e)

                if (e.response && e.response.data && e.response.data.errors) {
                    const errors = e.response.data.errors
                    const firstKey = Object.keys(errors)[0]
                    this.integrationError = errors[firstKey][0]
                } else if (e.response && e.response.data && e.response.data.message) {
                    this.integrationError = e.response.data.message
                } else {
                    this.integrationError = 'Não foi possível criar o pedido de integração.'
                }
            } finally {
                this.integrationLoadingDays = this.integrationLoadingDays.filter(item => item !== date)
            }
        },

        async fillBaseData() {
            this.loading = true

            const data = {
                tipo: "O"
            }

            await Obra.all(data).then((resp) => {
                this.obras = resp.data
            })

            User.getUserObras(this.$store.state.user.data.id).then((resp) => {
                this.obras_user_tem_acesso = resp.data
            })

            this.loading = false
        },

        toggleRow(date) {
            const index = this.expandedRows.indexOf(date)

            if (index > -1) {
                this.expandedRows.splice(index, 1)
            } else {
                this.expandedRows.push(date)
            }
        },

        isExpanded(date) {
            return this.expandedRows.includes(date)
        },

        formatDate(date) {
            return date
        },

        getRoleIcon(diary) {
            if (diary.creation_role === "encarregado") {
                return "mdi-account"
            }

            if (diary.creation_role === "manobrador") {
                return "mdi-steering"
            }

            return "mdi-account"
        },

        getRoleIconColorClass(diary) {
            if (diary.creation_role === "encarregado") {
                return "black--text"
            }

            if (diary.creation_role === "manobrador") {
                return "orange--text text--darken-2"
            }

            return "black--text"
        },

        getApprovalText(item) {
            let levelNeeded = parseInt(item.level_needed_to_approve)
            const currentLevel = item.current_approval ? parseInt(item.current_approval.level) : 0

            if (item.creation_role === 'encarregado' && levelNeeded === 3) {
                levelNeeded = 2
            }

            if (item.current_approval == null) {
                return `0 / ${levelNeeded}`
            }

            if (currentLevel >= levelNeeded) {
                return 'total'
            }

            return `${currentLevel} / ${levelNeeded}`
        },

        isApproved(diary) {
            return Boolean(Number(diary.full_approved))
        },

        hasKpi(diary) {
            return !!diary.diary_kpi_header_id
        },

        getApprovalResponsible(diary) {
            return diary.responsavel == null ? '-' : diary.responsavel
        },

        getWorkCode(diary) {
            return diary.chave || diary.work_name || "-"
        },

        canEditOrPreview(diary) {
            return diary.locked == false && diary.status === "DIARY"
        },

        edit(item){
            const isStandalone = window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone;

            // Significa que vem do ERP, vamos carregar vistas do ERP
            if(item.webAppLinkHash == null){
                window.open("/diarios/view/erp/equipment/" + item.Id, isStandalone ? '_self' : '_blank')
                return
            }

            if(item.creation_role == "encarregado"){
                window.open("/diarios/encarregado/editar/" + item.webAppLinkHash + "/" + item.id_user_creator, isStandalone ? '_self' : '_blank')
                return
            }else{
                window.open("/diarios/manobrador/" + item.webAppLinkHash + "/" + item.id_user_creator + '/eq', isStandalone ? '_self' : '_blank')
                return
            }
        },

        openPreviewDiary(id){
            this.previewDiary.id = id
            this.previewDiary.show = false
            this.previewDiary.show = true
        },
    },

    computed: {
        selectedWork() {
            if (!this.filter.work_id) {
                return null
            }

            return this.obras.find(obra => obra.Id === this.filter.work_id) || null
        },

        selectedWorkAllowsManualIntegration() {
            if (!this.selectedWork) {
                return false
            }

            return this.primaveraIntegrationActive === true && this.manualIntegrationActive === true
        },

        groupedDiaries() {
            if (!this.items.length) return []

            const groups = {}

            this.items.forEach(item => {
                if (!groups[item.data]) {
                    groups[item.data] = []
                }

                groups[item.data].push(item)
            })

            return Object.keys(groups)
                .sort((a, b) => new Date(b) - new Date(a))
                .map(date => ({
                    date,
                    diaries: groups[date]
                }))
        },

        calendarStatusMap() {
            const map = {}

            this.calendarStatuses.forEach(status => {
                const normalized = this.normalizeCalendarStatus(status)

                if (normalized && normalized.date) {
                    map[normalized.date] = normalized
                }
            })

            this.items.forEach(diary => {
                const status = this.getCalendarStatusFromDiary(diary)

                if (status && status.date && !map[status.date]) {
                    map[status.date] = status
                }
            })

            return map
        },

        crumbs() {
            return [
                {
                    text: "Consulta de diárias",
                    disabled: true,
                    to: "#",
                    exact: true,
                },
            ]
        }
    }
}
</script>

<style scoped>
.integration-unavailable-card {
    border: 1px solid #dbe5ef;
    border-radius: 18px;
    background: #f8fafc;
}
.integration-unavailable-illustration {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 150px;
    height: 150px;
    border-radius: 50%;
    background: #e7eff7;
}
.integration-unavailable-text {
    font-size: 16px;
    color: #516579;
    max-width: 650px;
    margin: auto;
}
.legend-wrapper {
    padding: 8px 0 16px 0;
}

.legend-pill {
    display: inline-flex;
    align-items: center;
    border: 1px solid #d6d6d6;
    border-radius: 16px;
    background: #fafafa;
    padding: 6px 12px;
    font-size: 13px;
    line-height: 1;
}

.calendar-card {
    border-radius: 12px;
}

.calendar-title {
    font-size: 16px;
    font-weight: 600;
}

.calendar-legend {
    gap: 10px;
    font-size: 12px;
    font-weight: 400;
}

.calendar-legend-item {
    display: inline-flex;
    align-items: center;
    gap: 5px;
}

.calendar-dot {
    width: 10px;
    height: 10px;
    border-radius: 50%;
    display: inline-block;
    border: 1px solid #cfcfcf;
}

.calendar-dot-none {
    background: #ffffff;
}

.calendar-dot-red {
    background: #ef5350;
    border-color: #ef5350;
}

.calendar-dot-yellow {
    background: #ffca28;
    border-color: #ffca28;
}

.calendar-dot-green {
    background: #66bb6a;
    border-color: #66bb6a;
}

.calendar-dot-purple {
    background: #ab47bc;
    border-color: #ab47bc;
}

.calendar-month {
    border: 1px solid #e0e0e0;
    border-radius: 10px;
    overflow: hidden;
    background: #ffffff;
}

.calendar-month-header {
    padding: 10px 12px;
    background: #f5f5f5;
    font-weight: 600;
    text-align: center;
    border-bottom: 1px solid #e0e0e0;
}

.calendar-weekdays {
    display: grid;
    grid-template-columns: repeat(7, 1fr);
    background: #fafafa;
    border-bottom: 1px solid #e0e0e0;
}

.calendar-weekday {
    padding: 8px 4px;
    text-align: center;
    font-size: 12px;
    font-weight: 600;
    color: #666;
}

.calendar-grid {
    display: grid;
    grid-template-columns: repeat(7, 1fr);
}

.calendar-day {
    min-height: 86px;
    border-right: 1px solid #eeeeee;
    border-bottom: 1px solid #eeeeee;
    padding: 6px;
    background: #ffffff;
    position: relative;
}

.calendar-day:nth-child(7n) {
    border-right: none;
}

.calendar-day-empty {
    background: #fafafa;
}

.calendar-day-outside-range {
    opacity: 0.35;
}

.calendar-day-number {
    font-size: 13px;
    font-weight: 600;
    color: #333;
}

.calendar-day-label {
    margin-top: 8px;
    font-size: 11px;
    line-height: 1.1;
    word-break: break-word;
}

.calendar-day-action {
    margin-top: 8px;
}

.calendar-integrate-btn {
    font-size: 10px;
    min-width: 70px !important;
    height: 22px !important;
    padding: 0 8px !important;
}

.calendar-day-no-color {
    background: #ffffff;
    border-left: 5px solid #d6d6d6;
}

.calendar-day-red {
    background: #ffebee;
    border-left: 5px solid #ef5350;
}

.calendar-day-yellow {
    background: #fff8e1;
    border-left: 5px solid #ffca28;
}

.calendar-day-green {
    background: #e8f5e9;
    border-left: 5px solid #66bb6a;
}

.calendar-day-purple {
    background: #f3e5f5;
    border-left: 5px solid #ab47bc;
}

.calendar-day-has-active-job {
    box-shadow: inset 0 0 0 2px rgba(97, 97, 97, 0.25);
}
</style>